import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { GLASS_CHIP, GLASS_POPOVER } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import { ERROR_RING } from './FormControls';

// The menu is portalled above dialogs (Modal is z-1100)
const MENU_Z_INDEX = 1200;

const optionClass = ({ isFocused, isSelected }) => {
  const base = 'tw-cursor-pointer tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm';
  if (isSelected) return `${base} tw-bg-teal-500/15 tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200`;
  if (isFocused) return `${base} tw-bg-slate-900/[0.05] tw-text-slate-900 dark:tw-bg-white/[0.07] dark:tw-text-white`;
  return `${base} tw-text-slate-700 dark:tw-text-slate-200`;
};

/**
 * Searchable single select (react-select) in the glass field style, for long
 * lists (customers, departments, centres, people). `value` is the stored
 * value; one missing from `options` stays visible as "(liste dışı)" until
 * changed. `onChange` gets the new value, or '' when cleared.
 */
const SearchSelect = ({ inputId, options, value, onChange, placeholder, isClearable, isDisabled, error, noOptionsText }) => {
  const { t } = useTranslation();
  const menuOpenRef = useRef(false);
  const hasValue = value !== undefined && value !== null && value !== '';
  const selected = options.find((option) => option.value === value)
    || (hasValue ? { value, label: t('dataTable.form.outOfList', { value: String(value) }) } : null);

  return (
    <Select
      inputId={inputId}
      unstyled
      options={options}
      value={selected}
      onChange={(option) => onChange(option ? option.value : '')}
      placeholder={placeholder || t('dataTable.form.choose')}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable
      aria-invalid={error ? true : undefined}
      noOptionsMessage={() => noOptionsText || t('dataTable.form.noOptions')}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      menuPlacement="auto"
      onMenuOpen={() => { menuOpenRef.current = true; }}
      onMenuClose={() => { menuOpenRef.current = false; }}
      // Escape with the list open only closes the list, not the dialog around it
      onKeyDown={(event) => {
        if (event.key === 'Escape' && menuOpenRef.current) event.nativeEvent.stopPropagation();
      }}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: MENU_Z_INDEX }) }}
      classNames={{
        control: ({ isFocused }) => `tw-min-h-[2.5rem] tw-rounded-xl tw-px-3 tw-text-base sm:tw-text-sm ${GLASS_CHIP} ${
          isFocused ? 'tw-ring-2 tw-ring-teal-500/40' : ''
        } ${error ? ERROR_RING : ''} ${isDisabled ? 'tw-opacity-60' : ''}`,
        valueContainer: () => 'tw-gap-1 tw-py-1',
        placeholder: () => 'tw-text-slate-400 dark:tw-text-slate-500',
        singleValue: () => 'tw-text-slate-800 dark:tw-text-slate-100',
        input: () => 'tw-text-slate-800 dark:tw-text-slate-100',
        indicatorsContainer: () => 'tw-gap-0.5',
        clearIndicator: () => 'tw-cursor-pointer tw-rounded-full tw-p-1 tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white',
        dropdownIndicator: () => 'tw-p-1 tw-text-slate-400',
        indicatorSeparator: () => 'tw-hidden',
        menu: () => `tw-mt-1.5 tw-overflow-hidden tw-rounded-2xl tw-p-1 tw-font-sans ${GLASS_POPOVER}`,
        menuList: () => 'tw-max-h-64 glass-scrollbar',
        option: optionClass,
        noOptionsMessage: () => 'tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400',
      }}
    />
  );
};

SearchSelect.propTypes = {
  inputId: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.any, label: PropTypes.node })).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  error: PropTypes.string,
  noOptionsText: PropTypes.string,
};

export default SearchSelect;
